import { Edit as EditIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface OrderItem {
  id: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  sku: string;
  subtotal: number;
  status?: string;
  notes?: string;
  retailer?: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;
}

interface OrderActionsProps {
  itemStatuses: any[];
  onUpdateItem: (itemId: string, updates: Partial<OrderItem>) => Promise<void>;
  onUpdateStatus: (
    itemId: string,
    newStatusId: number,
    notes?: string,
  ) => Promise<void>;
}

const OrderActions: React.FC<OrderActionsProps> = ({
  itemStatuses,
  onUpdateItem,
  onUpdateStatus,
}) => {
  const { t } = useTranslation();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
  const [editForm, setEditForm] = useState<Partial<OrderItem>>({});
  const [statusForm, setStatusForm] = useState<{
    newStatusId: number;
    notes?: string;
  }>({ newStatusId: 0 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleEditItem = (item: OrderItem) => {
    setSelectedItem(item);
    setEditForm({
      status: item.status || "",
      notes: item.notes || "",
      retailer: item.retailer || "",
      trackingNumber: item.trackingNumber || "",
      estimatedDelivery: item.estimatedDelivery || "",
    });
    setEditModalOpen(true);
  };

  const handleStatusUpdate = (item: OrderItem) => {
    setSelectedItem(item);
    setStatusForm({
      newStatusId: 0, // Will be set when user selects a status
      notes: item.notes || "",
    });
    setStatusModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedItem) return;

    try {
      setLoading(true);
      await onUpdateItem(selectedItem.id, editForm);
      setSnackbar({
        open: true,
        message: t("admin.orders.itemUpdatedSuccessfully"),
        severity: "success",
      });
      setEditModalOpen(false);
      setSelectedItem(null);
      setEditForm({});
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToUpdateItem"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusSubmit = async () => {
    if (!selectedItem || !statusForm.newStatusId) return;

    try {
      setLoading(true);
      await onUpdateStatus(
        selectedItem.id,
        statusForm.newStatusId,
        statusForm.notes,
      );
      setSnackbar({
        open: true,
        message: t("admin.orders.statusUpdatedSuccessfully"),
        severity: "success",
      });
      setStatusModalOpen(false);
      setSelectedItem(null);
      setStatusForm({ newStatusId: 0 });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToUpdateStatus"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      {/* Edit Item Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon />
            {t("admin.orders.editOrderItem")}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedItem.title}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.orders.status")}</InputLabel>
                    <Select
                      value={editForm.status || ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, status: e.target.value })
                      }
                      label={t("admin.orders.status")}
                    >
                      {itemStatuses.map((status) => (
                        <MenuItem key={status.id} value={status.name}>
                          {status.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.orders.retailer")}
                    value={editForm.retailer || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, retailer: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.orders.trackingNumber")}
                    value={editForm.trackingNumber || ""}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        trackingNumber: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.orders.estimatedDelivery")}
                    type="date"
                    value={editForm.estimatedDelivery || ""}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        estimatedDelivery: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("admin.orders.notes")}
                    multiline
                    rows={3}
                    value={editForm.notes || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, notes: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>{t("admin.orders.cancel")}</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? t("admin.orders.updating") : t("admin.orders.updateItem")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("admin.orders.updateItemStatus")}</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedItem.title}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.orders.status")}</InputLabel>
                    <Select
                      value={statusForm.newStatusId}
                      onChange={(e) =>
                        setStatusForm({
                          ...statusForm,
                          newStatusId: Number(e.target.value),
                        })
                      }
                      label={t("admin.orders.status")}
                    >
                      {itemStatuses.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("admin.orders.notes")}
                    multiline
                    rows={3}
                    value={statusForm.notes || ""}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, notes: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>{t("admin.orders.cancel")}</Button>
          <Button
            onClick={handleStatusSubmit}
            variant="contained"
            disabled={loading || !statusForm.newStatusId}
          >
            {loading ? t("admin.orders.updating") : t("admin.orders.updateStatus")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrderActions;
